/**
 * Article rédigé le 2026-09-06 à partir de sources web ouvertes et vérifiées automatiquement
 * (7 URL testées, révisé après re-vérification). Daté au moment des faits : 2026-09-01.
 * À relire avant/après mise en ligne. Format : voir src/blog/README.md
 */
export default {
  "slug": "sido-lyon-2026-iot-ia-robotique-besancon",
  "title": "SIDO Lyon 2026 : IoT, IA et robotique à deux heures de train de Besançon",
  "excerpt": "Les 16 et 17 septembre, la Cité Internationale de Lyon accueille la 12e édition du SIDO. Ce que propose ce salon IoT, IA et robotique facilement accessible depuis Besançon, et comment s'y préparer.",
  "seoTitle": "SIDO 2026 à Lyon : IoT, IA, robotique et XR",
  "seoDescription": "Les 16 et 17 septembre 2026, la Cité Internationale de Lyon réunit environ 300 exposants en IoT, IA et robotique. Programme, accès et repères pratiques.",
  "category": "evenements",
  "tags": [
    "SIDO",
    "Hors Besançon",
    "Robotique",
    "IoT",
    "Robotics Valley"
  ],
  "author": "jimmy",
  "publishedAt": "2026-09-01",
  "updatedAt": "2026-09-01",
  "featured": false,
  "cover": {
    "image": "/img/blog/anfiteatro-da-cite-internationale-3071097510.jpg",
    "credit": "Photo : Amphithéâtre de la Cité Internationale (Centre de congrès de Lyon) — Leandro Neumann Ciuffo, CC BY 2.0, via Wikimedia Commons",
    "alt": "Vue plongeante sur l'amphithéâtre du Centre de congrès de la Cité Internationale de Lyon pendant une conférence : gradins rouges remplis de participants professionnels, beaucoup avec un ordinateur portable, intervenants assis sur la scène en bois et grand écran de projection à droite."
  },
  "content": [
    {
      "type": "lead",
      "text": "Les 16 et 17 septembre 2026, la Cité Internationale de Lyon accueillera la douzième édition du SIDO, le salon professionnel consacré à la convergence entre objets connectés, intelligence artificielle, robotique et réalités étendues[^1]. À environ deux heures de train de Besançon Franche-Comté TGV, c'est l'un des rendez-vous de ce type les plus accessibles pour les acteurs locaux de l'électronique embarquée et [des microsystèmes](/blog/femto-st-besancon-capitale-microtechniques). Voici ce qui y sera montré, et ce qu'il faut savoir avant d'y aller."
    },
    {
      "type": "keyfacts",
      "facts": [
        {
          "label": "Quoi",
          "value": "SIDO 2026, 12e édition — salon IoT, IA, robotique et XR"
        },
        {
          "label": "Quand",
          "value": "Mercredi 16 et jeudi 17 septembre 2026, de 9 h à 18 h"
        },
        {
          "label": "Où",
          "value": "Cité Internationale de Lyon, 50 quai Charles-de-Gaulle, Lyon 6e"
        },
        {
          "label": "Qui",
          "value": "Organisé par Infopro Digital Trade Shows"
        },
        {
          "label": "Pour qui",
          "value": "Professionnels — entrée gratuite sur badge professionnel"
        }
      ]
    },
    {
      "type": "h2",
      "text": "Un salon centré sur la convergence des technologies"
    },
    {
      "type": "p",
      "text": "Le SIDO se présente comme le rendez-vous européen des décideurs de l'industrie, de la recherche et des services numériques[^3]. Pour l'édition 2026, l'organisateur annonce environ 300 exposants, 8 000 professionnels attendus, 70 conférences et 200 intervenants, autour de trois axes affichés : productivité, souveraineté et décarbonation[^1]. Une communication de l'organisateur diffusée en octobre 2025 avance de son côté plus de 380 exposants et 210 experts mobilisés sur le programme de conférences[^3] ; la presse spécialisée reprenait encore ce chiffre de 380 exposants en janvier 2026[^4]. Les deux séries émanent de l'organisateur — la page qui porte les secondes est signée Infopro Digital Trade Shows[^3] — et n'ont pas été rapprochées : à lire comme des ordres de grandeur."
    },
    {
      "type": "p",
      "text": "L'angle éditorial de cette édition est moins celui de la démonstration que celui de la mise en œuvre. Le programme aborde la convergence entre IoT, IA, XR et robotique comme un problème d'exécution industrielle : gouvernance des données, sécurité, compétences, dépendances technologiques, effets d'organisation et arbitrages énergétiques[^4]. Les formats privilégient les retours d'expérience et le partage de pratiques opérationnelles[^4] ; le détail des sessions est publié sur le programme en ligne du salon[^1]."
    },
    {
      "type": "h2",
      "text": "Deux espaces à repérer dans les allées"
    },
    {
      "type": "p",
      "text": "Le salon s'organise autour de deux zones thématiques identifiées, qui structurent aussi une partie du programme :"
    },
    {
      "type": "ul",
      "items": [
        "**Innorobo by SIDO**, consacré à l'écosystème robotique — applications industrielles, logistiques et médicales, mais aussi commerce, tourisme et collectivités[^1] — avec un déplacement assumé de la démonstration vers l'intégration dans des chaînes de production ou de service existantes[^4]. Des démonstrations de robotique collaborative et d'automatisation intelligente y sont annoncées[^3].",
        "**Impact by SIDO**, nouveauté de l'édition 2026, dédiée aux technologies « responsables, durables et à faible empreinte carbone »[^3]. Sobriété, efficacité énergétique et responsabilité sociale y sont traitées comme des paramètres d'architecture et de déploiement, à partir de retours d'expérience opérationnels[^4]."
      ]
    },
    {
      "type": "h2",
      "text": "Ce que la Bourgogne-Franche-Comté y joue"
    },
    {
      "type": "p",
      "text": "L'ancrage régional le plus visible est le **pavillon Robotics Valley**, un stand collectif de 48 m² dont l'appel à candidatures a été relayé par [La French Tech Bourgogne-Franche-Comté](https://lafrenchtechbfc.fr/appels-a-projets/exposez-au-sido-lyon-2026/). Il s'adressait aux PME, aux startups industrielles et robotiques et aux partenaires territoriaux, avec deux formules : un espace démonstration de 6 m² avec desk et zone démo, à 3 900 € HT et cinq badges, ou un pod simple face à 1 700 € HT et deux badges — dans les deux cas avec accès à la soirée networking. La date limite de candidature était fixée au 27 février 2026[^5]."
    },
    {
      "type": "p",
      "text": "Autre point d'entrée utile pour les entreprises du bassin bisontin : le programme national CAP'TRONIC, qui accompagne les PME sur l'électronique et le logiciel embarqué. Il était présent à l'édition 2025 avec un stand et des conférences sur les deux journées — transformation industrielle, gestion de l'énergie, cybersécurité, IA embarquée et bonnes pratiques IoT — ainsi que des démonstrations d'imagerie multimodale, de vision industrielle et d'infrastructures de données industrielles[^6]. Le détail de sa participation à l'édition 2026 — stand, conférences — n'était pas encore publié à la date de rédaction."
    },
    {
      "type": "p",
      "text": "*Analyse.* Aucune source publique ne recense à ce stade les exposants bisontins de l'édition 2026 : mieux vaut se garder d'annoncer une délégation locale. Ce qui est en revanche structurel : le tissu de Besançon — microtechniques, capteurs, temps-fréquence, sous-traitance de précision autour de [TEMIS](/blog/temis-technopole-startups-deeptech-besancon) — travaille en amont de presque toutes les briques exposées à Lyon. Un salon qui parle d'IA embarquée en production et de robotique intégrée est, pour ces entreprises, moins un lieu de veille qu'un lieu de qualification de clients et de partenaires d'intégration."
    },
    {
      "type": "h2",
      "text": "Guide pratique avant d'y aller"
    },
    {
      "type": "ol",
      "items": [
        "**Dates et horaires** : mercredi 16 et jeudi 17 septembre, de 9 h à 18 h les deux jours[^2].",
        "**Adresse** : centre de congrès de la Cité Internationale, 50 quai Charles-de-Gaulle, 69006 Lyon[^2].",
        "**Accès** : le site est à environ 10 minutes de la gare de Lyon Part-Dieu et 15 minutes de Perrache ; les lignes TCL C1, C5 et C23 desservent l'arrêt Cité Internationale. Des parkings (niveaux P1 et P2) sont accessibles sur place et une solution de covoiturage gratuite est proposée via Mobicoop[^2].",
        "**Badge** : l'entrée est gratuite sur badge professionnel[^3], l'inscription se faisant en ligne sur le [site du salon](https://www.sido-lyon.com/en/)[^1].",
        "**Programme** : les 70 conférences se répartissant sur deux jours seulement, il est préférable de sélectionner à l'avance trois ou quatre sessions et de garder le reste du temps pour les allées[^1][^4]."
      ]
    },
    {
      "type": "callout",
      "tone": "tip",
      "title": "Pourquoi ça compte pour Besançon",
      "text": "Le SIDO fait partie des rendez-vous français où se croisent capteurs, IA embarquée et robotique industrielle, et il est accessible depuis Besançon dans la journée : environ deux heures de train jusqu'à Lyon Part-Dieu, puis une dizaine de minutes jusqu'à la Cité Internationale[^2]. Pour une PME microtechnique du Doubs, cela change l'arbitrage par rapport à un salon plus lointain comme celui de Hanovre. Même sans stand, une visite bien préparée — conférences ciblées, rendez-vous pris en amont — reste un format économe en déplacement."
    },
    {
      "type": "h2",
      "text": "Un rendez-vous de proximité, à condition de le préparer"
    },
    {
      "type": "p",
      "text": "Le SIDO 2026 se présente moins comme une vitrine de ruptures technologiques que comme un lieu de retours d'expérience sur des déploiements réels[^4]. C'est ce qui le rend utile aux acteurs bisontins, dont le métier consiste rarement à inventer la brique d'IA et souvent à la faire tenir dans un produit contraint en énergie, en volume et en fiabilité. Rendez-vous les 16 et 17 septembre quai Charles-de-Gaulle, avec un programme lu à l'avance."
    }
  ],
  "references": [
    {
      "id": 1,
      "title": "SIDO Lyon 2026 — Le salon de la convergence IoT, IA, Robotique et XR",
      "publisher": "SIDO Lyon (Infopro Digital Trade Shows)",
      "url": "https://www.sido-lyon.com/en/",
      "type": "official",
      "date": ""
    },
    {
      "id": 2,
      "title": "SIDO — Informations pratiques (horaires, accès, adresse)",
      "publisher": "SIDO Lyon",
      "url": "https://www.sido-lyon.com/en/visit/useful-information",
      "type": "official",
      "date": ""
    },
    {
      "id": 3,
      "title": "SIDO Lyon revient les 16 et 17 septembre 2026 — communiqué de l'organisateur",
      "publisher": "PEI France (contenu signé Infopro Digital Trade Shows)",
      "url": "https://www.pei-france.com/article/sido-lyon-revient-les-16-et-17-septembre-2026/",
      "type": "official",
      "date": "2025-10-01"
    },
    {
      "id": 4,
      "title": "SIDO 2026, la convergence technologique confrontée à l'épreuve de l'industrialisation",
      "publisher": "FrenchWeb",
      "url": "https://www.frenchweb.fr/sido-2026-la-convergence-technologique-confrontee-a-lepreuve-de-lindustrialisation/459853",
      "type": "press",
      "date": "2026-01-23"
    },
    {
      "id": 5,
      "title": "Exposez au SIDO Lyon 2026 — pavillon Robotics Valley",
      "publisher": "La French Tech Bourgogne-Franche-Comté",
      "url": "https://lafrenchtechbfc.fr/appels-a-projets/exposez-au-sido-lyon-2026/",
      "type": "official",
      "date": "2026-02-24"
    },
    {
      "id": 6,
      "title": "SIDO Lyon 2025 — conférences et démonstrations CAP'TRONIC",
      "publisher": "CAP'TRONIC (JESSICA France)",
      "url": "https://www.captronic.fr/SIDO-Lyon-2025.html",
      "type": "official",
      "date": "2025-09-17"
    }
  ]
};
